import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { SeverityValueEnum } from '../enums/severity.enum';
import { CaseStatusEnum, NextActionStatusEmum } from '../enums/case-status.enum';
import { DataTableConfigSettings, DataTableSortBooleanEnum, DataTableStateModel } from '../components/dynamic-table/dynamic-table.model';
import { ListResponse } from '../models/list-response.model';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {

  constructor(private http: HttpClient) { }

  getLookupDropdown(params: any, problemRecordsForm?: boolean): Observable<any> {
    if (params.parentId === null && !problemRecordsForm) {
      return of([]);
    }
    const url = `/v1/dropdowns/lookup?type=${params.type}&parentId=${params.parentId}&filter=${params.filter}&pageSize=${params.pageSize}`;

    return this.http.get(url);
  }

  getSimpleDropdowns(): Observable<any> {
    return this.http.get('/v1/dropdowns/simple');
  }

  getDropdown(type: string): Observable<any> {
    return this.http.get(`/v1/dropdowns?type=${type}`);
  }

  setDataTableConfiguration(params: DataTableConfigSettings): Observable<DataTableConfigSettings> {
    return this.http.post<DataTableConfigSettings>('/v1/grid-settings', params);
  }

  getDataTableConfiguration(gridType: string): Observable<DataTableConfigSettings> {
    return this.http.get<DataTableConfigSettings>(`/v1/grid-settings?gridType=${gridType}`);
  }

  getDataTableConfigurationApplied(gridType: string): Observable<any> {
    return this.http.get<any>(`/v1/grid-settings/applied?gridType=${gridType}`)
  }

  getInitDataTableConfiguration(gridType: string): Observable<DataTableConfigSettings> {
    return this.getDataTableConfigurationApplied(gridType).pipe(
      switchMap(res => {
        return this.getDataTableConfigurationId(res);
      })
    );
  }

  getDataTableConfigurationId(id: number): Observable<DataTableConfigSettings> {
    if (!id) {
      return of([]) as any;
    }
    return this.http.get<DataTableConfigSettings>(`/v1/grid-settings?id=${id}`);
  }

  getDataTableFilterList(gridType: string, data: DataTableStateModel): Observable<ListResponse<any>> {
    let params: any = {
      skipCount: data.showItemsPerPage * (data.pageNumber - 1),
      takeCount: data.showItemsPerPage,
      isOrderByDescending: true
    };

    return this.http.post<ListResponse<any>>(`/v1/grid-settings/list?gridType=${gridType}`, params);
  }

  applyDataTableFilterList(gridType: string, id: number): Observable<any> {
    return this.http.post<any>(`/v1/grid-settings/apply?gridType=${gridType}&UserGridViewId=${id}`, {});
  }

  deleteSavedFilter(id: number): Observable<any> {
    return this.http.delete<any>(`/v1/grid-settings/${id}`);
  }

  updateSavedFilter(id: number, payload: { name: string; gridColumnSettings: string; gridFilterSettings: string; isGlobal: boolean; }): Observable<any> {
    return this.http.put<any>(`/v1/grid-settings?id=${id}`, payload);
  }

  setDefaultFilter(id: number) {
    const url = `/v1/grid-settings/default?id=${id}`;
    return this.http.post(url, {});
  }

  getDropdownConfigurations(params: any, field: string, skipCount = 0, takeCount = 50): Observable<any> {
    const url = `/v1/dropdown-configurations/search-entry?field=${field}&skipCount=${skipCount}&takeCount=${takeCount}`;
    return this.http.post(url, params);
  }


  getSingleDropdownConfigurations(params: any): Observable<any> {
    const url = `/v1/dropdown-configurations/search-single-configuration`;

    return this.http.post(url, params);
  }

  getSeverityItems() {
    const data = [
      {
        name: 'S1 = Tool Down',
        value: SeverityValueEnum.S1_TOOL_DOWN,
        backgroundColor: '#842029'
      },
      {
        name: 'S1 = Safety Issue (Pi)',
        value: SeverityValueEnum.S1_SAFETY_ISSUE_PI,
        backgroundColor: '#842029'
      },
      {
        name: 'S2 = High Impact Issue',
        value: SeverityValueEnum.S2_HIGH_IMPACT_ISSUE,
        backgroundColor: '#4B3F93'
      },
      {
        name: 'S3 = Medium Impact Issue',
        value: SeverityValueEnum.S3_MEDIUM_IMPACT_ISSUE,
        backgroundColor: '#E6BA00'
      },
      {
        name: 'S4 = Low Impact Issue',
        value: SeverityValueEnum.S4_LOW_IMPACT_ISSUE,
        backgroundColor: '#2D363A'
      },
      {
        name: 'S4 = Request For Information',
        value: SeverityValueEnum.S4_REQUEST_FOR_INFORMATION,
        backgroundColor: '#2D363A'
      }
    ];

    return data;
  }

  getCaseStageItems() {
    const data = [
      {
        name: 'New',
        value: 'New',
      },
      {
        name: 'Problem Definition',
        value: 'ProblemDefinition',
      },
      {
        name: 'Containment',
        value: 'Containment',
      },
      {
        name: 'Root Cause Analysis',
        value: 'RootCauseAnalysis',
      },
      {
        name: 'Developing Solution',
        value: 'DevelopingSolution',
      },
      {
        name: 'Testing Solution',
        value: 'TestingSolution',
      },
      {
        name: 'Implemented Solution',
        value: 'ImplementedSolution',
      },
      {
        name: 'Knowledge Sharing',
        value: 'KnowledgeSharing'
      },
      {
        name: 'Closed - Resolved',
        value: CaseStatusEnum.CLOSED_RESOLVED,
        statusValue: CaseStatusEnum.CLOSED_RESOLVED,
        visibleForAdminsOnly: true
      },
      {
        name: 'Closed - NotResolved',
        value: CaseStatusEnum.CLOSED_NOT_RESOLVED,
        statusValue: CaseStatusEnum.CLOSED_NOT_RESOLVED,
        visibleForAdminsOnly: true
      }
    ];

    return data;
  }

  getNextActionStatusItems() {
    const data = [
      {
        name: 'Initial Response Required',
        value: NextActionStatusEmum.InitialResponseRequired,
        statusValue: CaseStatusEnum.NEW
      },
      {
        name: 'Pending Customer',
        value: NextActionStatusEmum.PendingCustomer,
        statusValue: CaseStatusEnum.PENDING
      },
      {
        name: 'Pending Internal Case Contact',
        value: NextActionStatusEmum.PendingInternalCaseContact,
        statusValue: CaseStatusEnum.PENDING
      },
      {
        name: 'Wip',
        value: NextActionStatusEmum.Wip,
        statusValue: CaseStatusEnum.OPEN
      },
      {
        name: 'Wip - Apps',
        value: NextActionStatusEmum.Wip_Apps,
        statusValue: CaseStatusEnum.OPEN
      },
      {
        name: 'Wip - Stc',
        value: NextActionStatusEmum.Wip_Stc,
        statusValue: CaseStatusEnum.OPEN
      },
      {
        name: 'Wip - L4',
        value: NextActionStatusEmum.Wip_L4,
        statusValue: CaseStatusEnum.OPEN
      },
      {
        name: 'Pilot Deployment',
        value: NextActionStatusEmum.PilotDeployment,
        statusValue: CaseStatusEnum.VERIFY
      },
      {
        name: 'Solution Deployment',
        value: NextActionStatusEmum.SolutionDeployment,
        statusValue: CaseStatusEnum.VERIFY
      }
    ];

    return data;
  }

  getNextActionStatusItemsForFilter() {
    return [
      ...this.getNextActionStatusItems(),
      {
        name: 'Closed',
        value: 'Closed',
        statusValue: CaseStatusEnum.VERIFY
      }
    ]
  }

  getStatusItems() {
    const data = [
      {
        name: 'New',
        value: CaseStatusEnum.NEW,
      },
      {
        name: 'Open',
        value: CaseStatusEnum.OPEN,
      },
      {
        name: 'Pending',
        value: CaseStatusEnum.PENDING,
      },
      {
        name: 'Verify',
        value: CaseStatusEnum.VERIFY,
      },
      {
        name: 'Closed - Resolved',
        value: CaseStatusEnum.CLOSED_RESOLVED,
      },
      {
        name: 'Closed - NotResolved',
        value: CaseStatusEnum.CLOSED_NOT_RESOLVED,
      },
    ];

    return data;
  }

  getProblemRecordPriorityItems() {
    const data = [
      {
        name: 'Simple',
        value: 'Simple',
        backgroundColor: '#842029'
      },
      {
        name: 'Complex',
        value: 'Complex',
        backgroundColor: '#4B3F93'
      },
      {
        name: 'Complicated',
        value: 'Complicated',
        backgroundColor: '#2D363A'
      },
    ];

    return data;
  }

  getProblemRecordStageItems() {
    const data = [
      {
        name: 'New',
        value: 'New',
      },
      {
        name: 'Problem Definition',
        value: 'ProblemDefinition',
      },
      {
        name: 'Containment',
        value: 'Containment',
      },
      {
        name: 'Root Cause Analysis',
        value: 'RootCauseAnalysis',
      },
      {
        name: 'Developing Solution',
        value: 'DevelopingSolution',
      },
      {
        name: 'Testing Solution',
        value: 'TestingSolution',
      },
      {
        name: 'Implemented Solution',
        value: 'ImplementedSolution',
      },
      {
        name: 'Knowledge Sharing',
        value: 'KnowledgeSharing',
      }
    ];

    return data;
  }

  getProblemRecordStatusItems() {
    const data = [
      {
        name: 'New',
        value: 'New',
      },
      {
        name: 'Open',
        value: 'Open',
      },
      {
        name: 'Pending',
        value: 'Pending',
      },
      {
        name: 'Verify',
        value: 'Verify',
      },
    ];
    return data;
  }

  getBooleanDataTableColumnFilters() {
    const data = [
      {
        name: 'Yes',
        value: DataTableSortBooleanEnum.TRUE
      },
      {
        name: 'No',
        value: DataTableSortBooleanEnum.FALSE
      },
    ];

    return data;
  }

}
